/* CPK product system 2.0. Native product forms; no theme-specific cart drawer dependency. */
(() => {
  'use strict';
  if (window.__CPK2Loaded) return;
  window.__CPK2Loaded = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 780px)');
  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const richSelector = '.cpk2-accordion, .cpk2-description .cpk-desc-accordion';

  function animateDetails(details) {
    if (details.dataset.cpk2Animated) return;
    details.dataset.cpk2Animated = 'true';
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    let animation = null;
    let targetOpen = details.open;
    const reset = () => { details.style.removeProperty('height'); details.style.removeProperty('overflow'); };
    summary.addEventListener('click', event => {
      if (event.target.closest('a, button, input, select')) return;
      if (reduced.matches || !details.animate) {
        if (animation) { animation.cancel(); animation = null; reset(); }
        return;
      }
      event.preventDefault();
      const from = details.getBoundingClientRect().height;
      const next = animation ? !targetOpen : !details.open;
      if (animation) { animation.cancel(); animation = null; }
      targetOpen = next;
      reset();
      // Measure the browser's natural open/closed box, including borders and margins.
      details.open = next;
      const to = details.getBoundingClientRect().height;
      details.open = true;
      details.style.overflow = 'hidden';
      const current = details.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        duration: 220, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards'
      });
      animation = current;
      current.onfinish = () => {
        if (animation !== current) return;
        details.open = targetOpen;
        animation = null;
        current.cancel();
        reset();
      };
    });
  }

  /* Product-section navigation. Only these links are enhanced; manual scrolling
     and all other anchors keep their normal behaviour. */
  let navigationScroll = null;

  function navigationClearance(nav) {
    const manual = Number(nav?.dataset.cpk2ScrollOffset);
    if (Number.isFinite(manual) && manual > 0) return manual;
    let height = 0;
    const headers = document.querySelectorAll(
      'sticky-header, #shopify-section-header, .shopify-section-group-header-group, .header-wrapper, [data-sticky-header]'
    );
    headers.forEach(header => {
      const box = header.getBoundingClientRect();
      if (box.width < window.innerWidth * .45 || box.height > window.innerHeight * .6 || box.bottom <= 0) return;
      let candidate = header;
      let positioned = false;
      for (let i = 0; candidate && i < 3; i++, candidate = candidate.parentElement) {
        const style = getComputedStyle(candidate);
        if (style.position === 'fixed' || style.position === 'sticky') { positioned = true; break; }
      }
      if (positioned && box.top <= 16) height = Math.max(height, box.bottom);
    });
    return Math.round(height + 20);
  }

  function cancelNavigationScroll() {
    if (navigationScroll) navigationScroll.cancel();
  }

  function focusNavigationHeading(target) {
    const heading = target.querySelector('h2, h3') || target;
    const temporary = !heading.hasAttribute('tabindex');
    if (temporary) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    if (temporary) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
  }

  function goToProductSection(nav, target, link) {
    cancelNavigationScroll();
    const from = window.scrollY;
    const horizontal = window.scrollX;
    const clearance = navigationClearance(nav);
    const destination = () => Math.max(0, Math.min(
      target.getBoundingClientRect().top + window.scrollY - clearance,
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    ));
    const distance = Math.abs(destination() - from);
    const finish = () => {
      if (!target.isConnected) return;
      // Updating the address with the History API does not trigger a second jump.
      try {
        const url = new URL(window.location.href);
        url.hash = target.id;
        window.history.replaceState(window.history.state, '', url);
      } catch (_) { /* Native anchors remain available in restricted previews. */ }
      nav.querySelectorAll('a').forEach(item => {
        if (item === link) item.setAttribute('aria-current', 'location');
        else item.removeAttribute('aria-current');
      });
      focusNavigationHeading(target);
    };
    if (reduced.matches || distance < 4) {
      window.scrollTo({ top: destination(), left: horizontal, behavior: 'instant' });
      finish();
      return;
    }
    const duration = Math.min(620, Math.max(300, 260 + distance * .12));
    const started = performance.now();
    const controller = new AbortController();
    let frame = 0;
    let cancelled = false;
    const state = {
      cancel() {
        cancelled = true;
        cancelAnimationFrame(frame);
        controller.abort();
        if (navigationScroll === state) navigationScroll = null;
      }
    };
    navigationScroll = state;
    // Wheel, touch or navigation keys always give control straight back to the visitor.
    ['wheel', 'touchstart', 'pointerdown'].forEach(type => {
      window.addEventListener(type, () => state.cancel(), { passive: true, signal: controller.signal });
    });
    window.addEventListener('keydown', event => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Escape', 'Tab'].includes(event.key)) state.cancel();
    }, { signal: controller.signal });
    const tick = now => {
      if (cancelled || !target.isConnected) { state.cancel(); return; }
      const progress = Math.min(1, (now - started) / duration);
      const eased = progress < .5 ? 4 * progress ** 3 : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      // Re-measure the target while moving so a late-loaded image cannot leave
      // the destination behind. No document-wide smooth-scroll rule is added.
      window.scrollTo({ left: horizontal, top: from + (destination() - from) * eased, behavior: 'instant' });
      if (progress < 1) frame = requestAnimationFrame(tick);
      else { state.cancel(); finish(); }
    };
    frame = requestAnimationFrame(tick);
  }

  function refreshNavigation() {
    const anchors = $$(document, '.cpk2[data-cpk2-anchor]').filter(section =>
      section.id && !section.hidden && section.getClientRects().length
    );
    $$(document, '[data-cpk2-page-nav]').forEach(nav => {
      const signature = JSON.stringify(anchors.map(section => [section.id, section.dataset.cpk2Anchor]));
      if (nav.dataset.cpk2NavSignature !== signature) {
        const current = nav.querySelector('[aria-current="location"]')?.getAttribute('href');
        const focused = nav.contains(document.activeElement) ? document.activeElement.getAttribute('href') : null;
        const links = anchors.map(section => {
          const link = document.createElement('a');
          link.href = `#${section.id}`;
          link.textContent = section.dataset.cpk2Anchor;
          if (current === link.getAttribute('href')) link.setAttribute('aria-current', 'location');
          return link;
        });
        nav.replaceChildren(...links);
        nav.dataset.cpk2NavSignature = signature;
        if (focused) links.find(link => link.getAttribute('href') === focused)?.focus({ preventScroll: true });
      }
      nav.hidden = anchors.length === 0;
      if (nav.dataset.cpk2NavReady) return;
      nav.dataset.cpk2NavReady = 'true';
      nav.addEventListener('click', event => {
        const link = event.target.closest('a[href^="#"]');
        if (!link || !nav.contains(link) || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        let id;
        try { id = decodeURIComponent(link.hash.slice(1)); } catch (_) { return; }
        const target = document.getElementById(id);
        if (!target || !target.matches('.cpk2[data-cpk2-anchor]')) return;
        event.preventDefault();
        // Prevent a second delegated theme click handler starting another scroll.
        event.stopPropagation();
        goToProductSection(nav, target, link);
      }, { capture: true });
    });
  }

  /* Policy dialogs borrow Shopify's existing policy content. The document stays
     in place while the dialog body scrolls, and all original styles are restored. */
  let policyScrollLock = null;
  function lockPolicyScroll(dialog) {
    if (policyScrollLock) return;
    const root = document.documentElement;
    const body = document.body;
    const gap = Math.max(0, window.innerWidth - root.clientWidth);
    const saved = {
      dialog, x: window.scrollX, y: window.scrollY,
      overflow: root.style.getPropertyValue('overflow'),
      overflowPriority: root.style.getPropertyPriority('overflow'),
      padding: body.style.getPropertyValue('padding-right'),
      paddingPriority: body.style.getPropertyPriority('padding-right')
    };
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    root.style.setProperty('overflow', 'hidden', 'important');
    if (gap) body.style.setProperty('padding-right', `${padding + gap}px`, 'important');
    policyScrollLock = saved;
  }
  function unlockPolicyScroll(dialog) {
    const saved = policyScrollLock;
    if (!saved || saved.dialog !== dialog) return;
    const restore = (style, key, value, priority) => value ? style.setProperty(key, value, priority) : style.removeProperty(key);
    restore(document.documentElement.style, 'overflow', saved.overflow, saved.overflowPriority);
    restore(document.body.style, 'padding-right', saved.padding, saved.paddingPriority);
    policyScrollLock = null;
    if (Math.abs(window.scrollY - saved.y) > 1 || Math.abs(window.scrollX - saved.x) > 1) {
      window.scrollTo({ top: saved.y, left: saved.x, behavior: 'instant' });
    }
  }

  class CPK2Product extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.controller = new AbortController();
      this.sequence = 0;
      this.on('click', e => this.handleClick(e));
      this.on('change', e => {
        if (e.target.matches('[data-option-value-id]')) this.changeVariant(e.target);
        if (e.target.matches('input[name="quantity"]')) this.normaliseQuantity();
      });
      this.on('submit', e => {
        if (!e.target.matches('.cpk2-form')) return;
        const variant = this.getVariant();
        if (this.pending || !variant.available || !variant.id) { e.preventDefault(); return; }
        this.normaliseQuantity();
        // Native submission lets Shopify validate stock and show cart/errors normally.
      });
      this.on('keydown', e => {
        if (!e.target.closest('.cpk2-thumbs, .cpk2-gallery-arrow')) return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); this.step(e.key === 'ArrowLeft' ? -1 : 1, true); }
      });
      this.on('pointerdown', e => {
        if (e.pointerType !== 'touch' || !e.target.closest('[data-cpk2-stage]')) return;
        if (e.target.closest('button, video, iframe, model-viewer')) return;
        this.startTouch = { x: e.clientX, y: e.clientY };
      });
      this.on('pointerup', e => {
        if (!this.startTouch) return;
        const dx = e.clientX - this.startTouch.x;
        const dy = e.clientY - this.startTouch.y;
        this.startTouch = null;
        // Slightly lighter horizontal swipe; leave vertical page scrolling intact.
        if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.3) { this.step(dx < 0 ? 1 : -1); this.ignoreEnlargeUntil = Date.now() + 450; }
      });
      this.on('pointercancel', () => { this.startTouch = null; });
      $$ (this, '[data-cpk2-gallery-step]').forEach(button => { button.hidden = false; });
      this.activateInitial();
      this.enhancePurchase();
      this.setupFit();
      this.setupDialogs();
      this.observePurchase();
      this.scrollHandler = () => { if (!this.scrollQueued) { this.scrollQueued = true; requestAnimationFrame(() => { this.scrollQueued = false; this.updateSticky(); }); } };
      window.addEventListener('scroll', this.scrollHandler, { passive: true, signal: this.controller.signal });
      window.addEventListener('resize', this.scrollHandler, { passive: true, signal: this.controller.signal });
      window.addEventListener('pageshow', () => { this.pending = false; this.syncSticky(); this.updateSticky(); }, { signal: this.controller.signal });
      refreshNavigation();
      this.enableModels();
    }
    disconnectedCallback() {
      $$(this, '[data-cpk2-policy-dialog]').forEach(dialog => { if (dialog.open) dialog.close(); unlockPolicyScroll(dialog); });
      cancelNavigationScroll();
      this.galleryAnimation?.cancel();
      this.controller?.abort(); this.fetchController?.abort(); this.purchaseObserver?.disconnect();
      this._connected = false;
    }
    on(type, handler) { this.addEventListener(type, handler, { signal: this.controller.signal }); }
    getVariant() {
      try { return JSON.parse($(this, '[data-cpk2-current-variant]')?.textContent || '{}'); }
      catch (_) { return {}; }
    }
    status(message, error = false) {
      const node = $(this, '[data-cpk2-status]');
      if (node) { node.textContent = message; node.classList.toggle('is-error', error); }
    }
    enhancePurchase() {
      const options = $(this, '[data-cpk2-options]');
      if (options) options.hidden = false;
      $$(this, '[data-cpk2-quantity]').forEach(button => { button.hidden = false; });
      this.normaliseQuantity();
      this.syncSticky();
      // Shopify owns accelerated payment buttons; initialise newly inserted markup when supported.
      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
    }
    normaliseQuantity(delta = 0) {
      const input = $(this, 'input[name="quantity"]');
      if (!input) return;
      const min = Number(input.min) || 1;
      const step = Number(input.step) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const last = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
      let value = Number(input.value);
      if (!Number.isFinite(value)) value = min;
      value = min + Math.round((value - min) / step) * step + delta * step;
      input.value = String(Math.max(min, Math.min(value, last)));
      const minus = $(this, '[data-cpk2-quantity="-1"]');
      const plus = $(this, '[data-cpk2-quantity="1"]');
      if (minus) minus.disabled = input.disabled || Number(input.value) <= min || this.pending;
      if (plus) plus.disabled = input.disabled || Number(input.value) >= last || this.pending;
    }
    async changeVariant(source) {
      const seq = ++this.sequence;
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      const sourceId = source.id;
      const sourceValue = source.dataset.optionValueId;
      const values = $$(this, '[data-option-position]').sort((a,b) => Number(a.dataset.optionPosition)-Number(b.dataset.optionPosition)).map(group => $(group, '[data-option-value-id]:checked')?.dataset.optionValueId).filter(Boolean);
      const url = new URL(window.location.href);
      const productURL = new URL(this.dataset.productUrl, window.location.origin);
      if (source.dataset.productUrl) {
        const destination = new URL(source.dataset.productUrl, window.location.origin);
        // Combined listing siblings need their own product content, guide and FAQ.
        if (destination.pathname !== productURL.pathname) {
          for (const key of ['preview_theme_id', 'preview_key']) { if (url.searchParams.has(key)) destination.searchParams.set(key,url.searchParams.get(key)); }
          window.location.assign(destination.href); return;
        }
      }
      url.pathname = productURL.pathname;
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', values.join(','));
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.dataset.sectionId);
      this.pending = true;
      $(this, '[data-cpk2-purchase-state]')?.setAttribute('aria-busy','true');
      this.disablePurchasing();
      this.status('Updating your selection...');
      const reload = $(this, '[data-cpk2-reload]');
      if (reload) { reload.hidden = true; reload.href = url.href; }
      this.dispatchEvent(new CustomEvent('cpk2:selection-pending', { bubbles:true, detail:{productId:this.dataset.productId} }));
      try {
        const response = await fetch(fetchURL.href, { signal:this.fetchController.signal, credentials:'same-origin', headers:{'Accept':'text/html'} });
        if (!response.ok) throw new Error(`Selection request failed (${response.status})`);
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const freshRoot = doc.getElementById(this.id);
        const fresh = freshRoot?.querySelector('[data-cpk2-purchase-state]');
        if (!fresh || seq !== this.sequence) { if (seq !== this.sequence) return; throw new Error('Product section was not returned'); }
        const old = $(this, '[data-cpk2-purchase-state]');
        const previousQuantity = $(this, 'input[name="quantity"]')?.value;
        old.replaceWith(document.importNode(fresh,true));
        this.pending = false;
        const quantity = $(this, 'input[name="quantity"]');
        if (quantity && previousQuantity) quantity.value = previousQuantity;
        this.enhancePurchase();
        this.observePurchase();
        const variant = this.getVariant();
        if (variant.mediaId) this.activate(String(variant.mediaId));
        if (variant.id) { url.searchParams.delete('option_values'); url.searchParams.set('variant',String(variant.id)); }
        window.history.replaceState({},'',url);
        const focus = document.getElementById(sourceId) || $$(this,'[data-option-value-id]').find(input=>input.dataset.optionValueId===sourceValue);
        if (focus) focus.focus({ preventScroll:true });
        this.status(variant.id ? (variant.available ? 'Selection updated.' : 'This option is sold out.') : 'This combination is unavailable. Choose another option.');
        this.dispatchEvent(new CustomEvent('cpk2:variant-change',{bubbles:true,detail:{productId:this.dataset.productId,variant,url:url.href}}));
      } catch (error) {
        if (error.name === 'AbortError' || seq !== this.sequence) return;
        // Fail closed: never leave the previously selected variant purchasable.
        this.pending = true;
        this.disablePurchasing();
        this.status('Your selection could not be updated. Please try another option or reload the selected option below.',true);
        if (reload) reload.hidden = false;
        this.dispatchEvent(new CustomEvent('cpk2:selection-error',{bubbles:true,detail:{productId:this.dataset.productId}}));
      }
    }
    disablePurchasing() {
      const id = $(this,'.cpk2-form input[name="id"]');
      if (id) { id.disabled = true; id.value = ''; }
      const button = $(this,'[data-cpk2-add]');
      if (button) { button.disabled = true; button.textContent = 'Updating...'; }
      const express = $(this,'.cpk2-express');
      if (express) express.hidden = true;
      const sticky = $(this,'[data-cpk2-sticky-add]');
      if (sticky) { sticky.disabled = true; sticky.textContent = 'Updating...'; }
    }
    handleClick(e) {
      const policy = e.target.closest('[data-cpk2-policy-open]');
      if (policy) {
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        const dialog = $(this, `[data-cpk2-policy-dialog="${policy.dataset.cpk2PolicyOpen}"]`);
        if (!dialog?.showModal) return;
        e.preventDefault();
        e.stopPropagation();
        cancelNavigationScroll();
        this.openDialog(dialog, policy);
        return;
      }
      const thumb = e.target.closest('[data-cpk2-thumb]');
      if (thumb) { e.preventDefault(); this.activate(thumb.dataset.cpk2Thumb); return; }
      const arrow = e.target.closest('[data-cpk2-gallery-step]');
      if (arrow) { this.step(Number(arrow.dataset.cpk2GalleryStep)); return; }
      const quantity = e.target.closest('[data-cpk2-quantity]');
      if (quantity) { this.normaliseQuantity(Number(quantity.dataset.cpk2Quantity)); return; }
      const enlarge = e.target.closest('[data-cpk2-enlarge]');
      if (enlarge) {
        if (Date.now() < (this.ignoreEnlargeUntil || 0)) { e.preventDefault(); return; }
        const dialog = $(this,'[data-cpk2-photo-dialog]');
        if (!dialog?.showModal) return;
        e.preventDefault();
        const img = $(dialog,'[data-cpk2-large-photo]');
        img.src = enlarge.href; img.alt = enlarge.querySelector('img')?.alt || 'Product photograph'; img.hidden = false;
        this.openDialog(dialog,enlarge); return;
      }
      const load = e.target.closest('[data-cpk2-load-media]');
      if (load) {
        const template = load.nextElementSibling;
        if (template instanceof HTMLTemplateElement) { load.parentElement.insertBefore(template.content.cloneNode(true),load); load.hidden = true; }
        return;
      }
      if (e.target.closest('[data-cpk2-fit-open]')) { this.openDialog($(this,'[data-cpk2-fit-dialog]'),e.target.closest('button')); return; }
      const close = e.target.closest('[data-cpk2-close-dialog]');
      if (close) { close.closest('dialog').close(); return; }
      if (e.target.closest('[data-cpk2-sticky-add]')) {
        const form = $(this,'.cpk2-form');
        const submit = $(this,'[data-cpk2-add]');
        if (form && submit && !submit.disabled && form.reportValidity()) form.requestSubmit(submit);
      }
    }
    activateInitial() {
      const slides = $$(this,'.cpk2-slide');
      const active = slides.find(slide=>!slide.hidden) || slides[0];
      if (active) this.activate(active.dataset.mediaId,false);
    }
    activate(id, scroll = true, direction = 0) {
      const slides = $$(this,'.cpk2-slide');
      const active = slides.find(slide=>slide.dataset.mediaId===String(id));
      if (!active) return;
      const previous = slides.find(slide=>!slide.hidden);
      const changed = Boolean(previous && previous !== active);
      // Stop the preceding transition if someone rapidly clicks or swipes.
      this.galleryAnimation?.cancel();
      this.galleryAnimation = null;
      slides.forEach(slide=>{
        if (slide !== active) {
          $$(slide,'video').forEach(video=>video.pause());
          // Unload external players so audio cannot continue in hidden slides.
          if (slide.dataset.mediaType === 'external_video') {
            $$(slide,'iframe').forEach(frame=>frame.remove());
            const poster = $(slide,'[data-cpk2-load-media]'); if(poster) poster.hidden = false;
          }
        }
        slide.hidden = slide !== active;
      });
      // Image-only, gentle reveal; no animation on initial load or for videos/3D.
      if (changed && !reduced.matches && active.dataset.mediaType === 'image' && active.animate) {
        const offset = direction < 0 ? -10 : direction > 0 ? 10 : 0;
        const frames = offset
          ? [{ opacity: .55, transform: `translateX(${offset}px)` }, { opacity: 1, transform: 'translateX(0)' }]
          : [{ opacity: .45 }, { opacity: 1 }];
        const animation = active.animate(frames, { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' });
        this.galleryAnimation = animation;
        animation.onfinish = () => { if (this.galleryAnimation === animation) this.galleryAnimation = null; };
      }
      let selected;
      $$(this,'[data-cpk2-thumb]').forEach(thumb=>{
        const isSelected = thumb.dataset.cpk2Thumb === String(id);
        if(isSelected){thumb.setAttribute('aria-current','true');selected=thumb;}else thumb.removeAttribute('aria-current');
      });
      const count = $(this,'[data-cpk2-counter]'); if(count) count.textContent = `${slides.indexOf(active)+1} / ${slides.length}`;
      const strip = $(this,'.cpk2-thumbs');
      if(scroll && strip && selected) {
        const offset = selected.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft - (strip.clientWidth-selected.offsetWidth)/2;
        strip.scrollTo({left:offset,behavior:reduced.matches?'auto':'smooth'});
      }
    }
    step(delta,focus=false) {
      const slides = $$(this,'.cpk2-slide'); if(slides.length<2)return;
      const current = Math.max(0,slides.findIndex(slide=>!slide.hidden));
      const next = slides[(current+delta+slides.length)%slides.length];
      this.activate(next.dataset.mediaId, true, delta);
      if(focus) $(this,`[data-cpk2-thumb="${next.dataset.mediaId}"]`)?.focus({preventScroll:true});
    }
    setupFit() {
      const fallback = $(this,'[data-cpk2-fit-content]');
      const dialog = $(this,'[data-cpk2-fit-dialog]');
      if(!fallback || !dialog?.showModal)return;
      const body = $(fallback,'.cpk2-answer');
      $(dialog,'[data-cpk2-fit-dialog-body]').innerHTML = body.innerHTML;
      fallback.hidden = true;
      $(this,'[data-cpk2-fit-open]').hidden = false;
    }
    setupDialogs() {
      $$(this, '[data-cpk2-policy-open]').forEach(link => {
        const dialog = $(this, `[data-cpk2-policy-dialog="${link.dataset.cpk2PolicyOpen}"]`);
        if (dialog?.showModal) {
          link.setAttribute('aria-haspopup', 'dialog');
          if (dialog.id) link.setAttribute('aria-controls', dialog.id);
        }
      });
      $$(this,'dialog').forEach(dialog=>{
        dialog.addEventListener('close',()=>{unlockPolicyScroll(dialog);dialog._returnFocus?.focus({preventScroll:true});this.updateSticky();},{signal:this.controller.signal});
        dialog.addEventListener('click',e=>{
          if(e.target!==dialog)return;
          const r=dialog.getBoundingClientRect();
          if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();
        },{signal:this.controller.signal});
      });
    }
    openDialog(dialog,trigger) {
      if (!dialog?.showModal || dialog.open) return;
      const isPolicy = dialog.hasAttribute('data-cpk2-policy-dialog');
      if (isPolicy) lockPolicyScroll(dialog);
      dialog._returnFocus = trigger;
      try { dialog.showModal(); } catch (_) { if (isPolicy) unlockPolicyScroll(dialog); return; }
      if (isPolicy) {
        const content = $(dialog, '.cpk2-policy-body');
        if (content) content.scrollTop = 0;
        $(dialog, '[data-cpk2-close-dialog]')?.focus({ preventScroll:true });
        if (!reduced.matches && dialog.animate) dialog.animate(
          [{ opacity:0, transform:'translateY(8px)' }, { opacity:1, transform:'translateY(0)' }],
          { duration:180, easing:'ease-out' }
        );
      }
      this.updateSticky();
    }
    observePurchase() {
      this.purchaseObserver?.disconnect();
      const row=$(this,'[data-cpk2-primary-actions]');
      if(!row||!('IntersectionObserver'in window))return;
      this.purchaseObserver=new IntersectionObserver(()=>this.updateSticky());
      this.purchaseObserver.observe(row);
      this.syncSticky();
    }
    syncSticky() {
      const button=$(this,'[data-cpk2-sticky-add]');if(!button)return;
      const variant=this.getVariant();
      button.disabled = Boolean(this.pending || !variant.available || !variant.id);
      button.textContent = this.pending?'Updating...':variant.available?'Add to cart':variant.id?'Sold out':'Unavailable';
      const price=$(this,'[data-cpk2-sticky-price]');if(price)price.textContent=variant.price||'';
      this.updateSticky();
    }
    updateSticky() {
      const sticky=$(this,'[data-cpk2-sticky]');const row=$(this,'[data-cpk2-primary-actions]');
      if(!sticky||!row)return;
      const atBottom=window.scrollY+window.innerHeight>=document.documentElement.scrollHeight-100;
      sticky.hidden=!(mobile.matches && row.getBoundingClientRect().bottom<0 && !$(this,'dialog[open]') && !atBottom);
    }
    enableModels() {
      if(!$(this,'model-viewer'))return;
      if(window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:(errors)=>{
        if(errors || !window.Shopify.ModelViewerUI || !this.isConnected)return;
        $$(this,'model-viewer').forEach(model=>{if(!model._cpk2UI)model._cpk2UI=new window.Shopify.ModelViewerUI(model);});
      }}]);
    }
  }

  class CPK2Guide extends HTMLElement {
    connectedCallback(){
      if(this._connected)return;this._connected=true;this.controller=new AbortController();
      const panels=$$(this,'[data-cpk2-guide-panel]');const controls=$(this,'[data-cpk2-guide-controls]');
      if(!panels.length||!controls)return;
      controls.hidden=false;$(this,'.cpk2-guide-box').classList.add('is-enhanced');
      panels.forEach((p,i)=>p.hidden=i!==0);
      this.addEventListener('click',e=>{
        const button=e.target.closest('[data-cpk2-guide-choice]');if(!button)return;
        const id=button.dataset.cpk2GuideChoice;
        $$(this,'[data-cpk2-guide-choice]').forEach(b=>b.setAttribute('aria-pressed',String(b===button)));
        panels.forEach(p=>{
          p.hidden=p.dataset.cpk2GuidePanel!==id;
          if(!p.hidden&&!reduced.matches&&p.animate)p.animate([{opacity:.55},{opacity:1}],{duration:160,easing:'ease-out'});
        });
      },{signal:this.controller.signal});
      refreshNavigation();
    }
    disconnectedCallback(){this.controller?.abort();this._connected=false;}
  }

  class CPK2ProductInfo extends HTMLElement {
    connectedCallback(){
      if(this._connected)return;this._connected=true;this.controller=new AbortController();this.version=0;
      const specs=$(this,'[data-cpk2-responsive-specs]');
      if(specs){specs.open=!mobile.matches;specs.querySelector('summary').addEventListener('click',()=>{this.specsTouched=true;},{signal:this.controller.signal});}
      this.resize=()=>{if(specs&&!this.specsTouched)specs.open=!mobile.matches;};
      window.addEventListener('resize',this.resize,{signal:this.controller.signal,passive:true});
      document.addEventListener('cpk2:selection-pending',e=>{
        if(e.detail.productId!==this.dataset.productId)return;
        this.version++;this.fetchController?.abort();specs?.setAttribute('aria-busy','true');
        const status=$(this,'[data-cpk2-spec-status]');if(status)status.textContent='Updating selected specifications...';
      },{signal:this.controller.signal});
      document.addEventListener('cpk2:selection-error',e=>{if(e.detail.productId===this.dataset.productId){const status=$(this,'[data-cpk2-spec-status]');if(status)status.textContent='Specifications will update when your selection is confirmed.';}},{signal:this.controller.signal});
      document.addEventListener('cpk2:variant-change',e=>{if(e.detail.productId===this.dataset.productId)this.update(e.detail.url);},{signal:this.controller.signal});
    }
    async update(selectionURL){
      const version=++this.version;this.fetchController?.abort();this.fetchController=new AbortController();
      const url=new URL(selectionURL);url.searchParams.set('section_id',this.dataset.sectionId);
      const specs=$(this,'[data-cpk2-responsive-specs]');const status=$(this,'[data-cpk2-spec-status]');
      specs?.setAttribute('aria-busy','true');
      try{
        const response=await fetch(url,{signal:this.fetchController.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if(!response.ok)throw new Error('Specifications unavailable');
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const next=doc.getElementById(this.id)?.querySelector('[data-cpk2-specs-body]');
        if(version!==this.version)return;if(!next)throw new Error('Missing specification section');
        $(this,'[data-cpk2-specs-body]').replaceWith(document.importNode(next,true));
        specs?.removeAttribute('aria-busy');if(status)status.textContent='';
      }catch(error){if(error.name==='AbortError'||version!==this.version)return;if(status)status.textContent='Specifications could not refresh. Reload this page before relying on size-specific details.';}
    }
    disconnectedCallback(){this.controller?.abort();this.fetchController?.abort();this._connected=false;}
  }

  class CPK2Recommendations extends HTMLElement {
    connectedCallback(){
      if(this._connected)return;this._connected=true;
      if($(this,'[data-cpk2-recommendations-content]'))return;
      this.controller=new AbortController();
      const load=()=>{this.observer?.disconnect();this.load();};
      if('IntersectionObserver'in window){this.observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting))load();},{rootMargin:'300px'});this.observer.observe(this);}else load();
    }
    async load(){
      try{
        const response=await fetch(this.dataset.url,{signal:this.controller.signal,credentials:'same-origin'});
        if(!response.ok)return;
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const content=doc.querySelector('[data-cpk2-recommendations-content]');
        if(content)this.replaceChildren(document.importNode(content,true));
      }catch(error){if(error.name!=='AbortError')console.warn('CPK: optional recommendations unavailable.');}
    }
    disconnectedCallback(){this.controller?.abort();this.observer?.disconnect();this._connected=false;}
  }

  customElements.define('cpk2-product',CPK2Product);
  customElements.define('cpk2-guide',CPK2Guide);
  customElements.define('cpk2-product-info',CPK2ProductInfo);
  customElements.define('cpk2-recommendations',CPK2Recommendations);
  function initialise(root=document){$$(root,richSelector).forEach(animateDetails);refreshNavigation();}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initialise());else initialise();
  document.addEventListener('shopify:section:load',e=>initialise(e.target));
  document.addEventListener('shopify:section:unload',()=>{cancelNavigationScroll();requestAnimationFrame(refreshNavigation);});
  reduced.addEventListener?.('change', cancelNavigationScroll);
  document.addEventListener('shopify:section:reorder',refreshNavigation);
})();
